import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Grid,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip,
  Stack,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import HomeRepairServiceOutlinedIcon from "@mui/icons-material/HomeRepairServiceOutlined";
import SearchIcon from "@mui/icons-material/Search";
import * as MuiIcons from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import api, { API_BASE_URL } from "../../utils/api";
import AddWorkOrderSmart from "./addWorkorderheritage";
import MemberWorkordersDetailsheritage from "./MemberWorkordersDetailsheritage";

const ITEMS_PER_PAGE = 5;

const cardSx = {
  borderRadius: "16px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const nestedCardSx = {
  p: 2,
  borderRadius: "14px",
  border: "1px solid rgba(47,99,173,0.08)",
  backgroundColor: "rgba(255,255,255,0.98)",
  boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
  height: "100%",
  display: "flex",
  flexDirection: "column",
};

// ─── Green Sheet Card ─────────────────────────────────────────────────────────

const GreenSheetCard = ({ order, onView, statuses }) => {
  const theme = useTheme();
  const [thumbUrl, setThumbUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/workorder-files/${order.order_id}`)
      .then((res) => {
        if (cancelled) return;
        const img = (res.data || []).find((f) => f.file_type?.startsWith("image/"));
        if (img) {
          const raw = img.file_url || "";
          const url = raw.startsWith("http")
            ? raw
            : `${API_BASE_URL}/workorderimages/${encodeURIComponent(raw.split("/").pop())}`;
          setThumbUrl(url);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [order.order_id]);

  const getStatusInfo = useCallback(
    (statusName) => statuses.find((s) => s.statusname === statusName),
    [statuses]
  );

  const renderStatusChip = (o) => {
    const statusInfo = getStatusInfo(o.status);
    const normalized = String(o?.status || "").toLowerCase();
    let chipStyles = { color: "#2f63ad", backgroundColor: "rgba(47,99,173,0.10)", fontWeight: 700 };
    if (["resolved", "closed", "completed"].includes(normalized) || o?.resolved_at) {
      chipStyles = { color: "#15803d", backgroundColor: "rgba(34,197,94,0.12)", fontWeight: 700 };
    } else if (normalized) {
      chipStyles = { color: "#b45309", backgroundColor: "rgba(245,158,11,0.12)", fontWeight: 700 };
    }
    return (
      <Chip size="small" label={statusInfo?.statusname || o.status || "Unknown"} sx={chipStyles} />
    );
  };

  const renderStatusLine = (o) => {
    const statusInfo = getStatusInfo(o.status);
    const IconComponent = statusInfo?.icon && MuiIcons[statusInfo.icon];
    const themeColor =
      statusInfo?.color && theme.palette[statusInfo.color]
        ? theme.palette[statusInfo.color].main
        : statusInfo?.color || "#5f6b7a";
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {IconComponent && <IconComponent fontSize="small" sx={{ color: themeColor }} />}
        <Typography sx={{ color: themeColor, fontWeight: 700, fontSize: "0.875rem" }}>
          {statusInfo?.statusname || o.status || "Unknown"}
        </Typography>
      </Box>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        ...nestedCardSx,
        p: 0,
        flexDirection: "row",
        overflow: "hidden",
      }}
    >
      {/* Image thumbnail (left) */}
      {thumbUrl && (
        <Box
          component="img"
          src={thumbUrl}
          alt="Green Sheet"
          sx={{
            width: 110,
            minWidth: 110,
            objectFit: "cover",
            borderRadius: "14px 0 0 14px",
            flexShrink: 0,
            display: "block",
          }}
        />
      )}

      {/* Card content (right) */}
      <Box sx={{ flex: 1, p: 2, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1.5,
            mb: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142", mb: 0.25 }}>
              Green Sheet #{order.order_id}
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", fontWeight: 600, mb: 0.5 }}>
              {order.type_name || "N/A"}
              {order.subtype_name ? ` • ${order.subtype_name}` : ""}
            </Typography>
          </Box>
          {renderStatusChip(order)}
        </Box>

        <Box sx={{ mb: 1.5 }}>{renderStatusLine(order)}</Box>

        <Typography variant="body2" sx={{ color: "#6b778c", mb: 2, flexGrow: 1 }}>
          {order.description || "No description available."}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "auto" }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={onView}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
          >
            View Details
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const WorkOrders = ({ workOrders = [], propertyId }) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:900px)");
  const [mobileDetail, setMobileDetail] = useState({ open: false, id: null });

  const [statuses, setStatuses] = useState([]);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [sortOrder, setSortOrder] = useState("newest");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await api.get(`/statuses`);
        setStatuses(response.data || []);
      } catch (error) {
        console.error("Failed to fetch statuses:", error);
      }
    };
    fetchStatuses();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [sortOrder, searchTerm, statusFilter]);

  const uniqueStatuses = useMemo(
    () => [...new Set(workOrders.map((o) => o.status).filter(Boolean))].sort(),
    [workOrders]
  );

  const isResolvedStatus = (order) => {
    const normalized = String(order?.status || "").toLowerCase();
    return (
      !!order?.resolved_at ||
      ["resolved", "closed", "completed"].includes(normalized)
    );
  };

  const resolvedCount = useMemo(() => {
    return workOrders.filter((order) => isResolvedStatus(order)).length;
  }, [workOrders]);

  const openCount = workOrders.length - resolvedCount;

  const filteredWorkOrders = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    let result = workOrders;

    if (q) {
      result = result.filter((order) =>
        String(order.order_id || "").includes(q) ||
        String(order.type_name || "").toLowerCase().includes(q) ||
        String(order.subtype_name || "").toLowerCase().includes(q) ||
        String(order.description || "").toLowerCase().includes(q) ||
        String(order.status || "").toLowerCase().includes(q)
      );
    }

    if (statusFilter) {
      result = result.filter((o) => o.status === statusFilter);
    }

    return result;
  }, [workOrders, searchTerm, statusFilter]);

  const sortedWorkOrders = useMemo(() => {
    return [...filteredWorkOrders].sort((a, b) => {
      const dateA = new Date(a.created_at || a.updated_at || 0);
      const dateB = new Date(b.created_at || b.updated_at || 0);
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });
  }, [filteredWorkOrders, sortOrder]);

  const isFiltering = searchTerm.trim() !== "" || statusFilter !== null;
  const previewItems = isFiltering ? sortedWorkOrders.slice(0, 4) : sortedWorkOrders.slice(0, 2);

  const paginatedWorkOrders = sortedWorkOrders.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    setPage(1);
  };

  return (
    <>
      <AddWorkOrderSmart
        open={openAddModal}
        handleClose={() => setOpenAddModal(false)}
        propertyId={propertyId}
        onWorkOrderAdded={() => {
          setShowSuccessSnackbar(true);
          window.location.reload();
        }}
      />

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setShowSuccessSnackbar(false)}
          sx={{ width: "100%" }}
        >
          Green Sheet created successfully!
        </Alert>
      </Snackbar>

      <Box sx={{ width: "100%" }}>
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
            Green Sheet
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Review submitted Green Sheet, status updates, and recent activity.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2.5} alignItems="stretch">
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ ...cardSx, p: 2.25, minHeight: 128 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1.5 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>
                      Total Green Sheets
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>
                      {workOrders.length}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 42, height: 42, borderRadius: "12px", backgroundColor: "rgba(47,99,173,0.10)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <HomeRepairServiceOutlinedIcon sx={{ color: "#2f63ad" }} />
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ ...cardSx, p: 2.25, minHeight: 128 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1.5 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>
                      Open Green Sheets
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>
                      {openCount}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 42, height: 42, borderRadius: "12px", backgroundColor: "rgba(245,158,11,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <PendingActionsOutlinedIcon sx={{ color: "#d97706" }} />
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ ...cardSx, p: 2.25, minHeight: 128 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1.5 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>
                      Resolved
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>
                      {resolvedCount}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 42, height: 42, borderRadius: "12px", backgroundColor: "rgba(34,197,94,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <CheckCircleOutlineIcon sx={{ color: "#15803d" }} />
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Filter toolbar */}
          <Box sx={{ mt: 2.5 }}>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 1.25 }}>
              <TextField
                size="small"
                placeholder="Search green sheets…"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "#6b778c" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: "1 1 200px", "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortOrder}
                  label="Sort By"
                  onChange={(e) => { setSortOrder(e.target.value); setPage(1); }}
                  sx={{ borderRadius: "10px" }}
                >
                  <MenuItem value="newest">Newest First</MenuItem>
                  <MenuItem value="oldest">Oldest First</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {uniqueStatuses.length > 0 && (
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                <Chip
                  label="All"
                  size="small"
                  onClick={() => { setStatusFilter(null); setPage(1); }}
                  sx={{
                    fontWeight: 600,
                    borderRadius: "8px",
                    backgroundColor: statusFilter === null ? "rgba(47,99,173,0.12)" : "rgba(0,0,0,0.05)",
                    color: statusFilter === null ? "#2f63ad" : "#5f6b7a",
                    border: statusFilter === null ? "1px solid rgba(47,99,173,0.3)" : "1px solid transparent",
                  }}
                />
                {uniqueStatuses.map((s) => (
                  <Chip
                    key={s}
                    label={s}
                    size="small"
                    onClick={() => { setStatusFilter(s === statusFilter ? null : s); setPage(1); }}
                    sx={{
                      fontWeight: 600,
                      borderRadius: "8px",
                      backgroundColor: statusFilter === s ? "rgba(47,99,173,0.12)" : "rgba(0,0,0,0.05)",
                      color: statusFilter === s ? "#2f63ad" : "#5f6b7a",
                      border: statusFilter === s ? "1px solid rgba(47,99,173,0.3)" : "1px solid transparent",
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
            Recent Green Sheets
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            A quick preview of the latest green sheets for this account.
          </Typography>
        </Box>

        {workOrders.length === 0 ? (
          <Paper elevation={0} sx={{ ...cardSx, p: 3, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
              No green sheets found for this account.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => setOpenAddModal(true)}
              sx={{
                backgroundColor: "#2f63ad",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": { backgroundColor: "#1f4f94", boxShadow: "0 8px 20px rgba(47,99,173,0.22)" },
              }}
            >
              Create New Green Sheet
            </Button>
          </Paper>
        ) : filteredWorkOrders.length === 0 ? (
          <Paper elevation={0} sx={{ ...cardSx, p: 3, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              No green sheets match your search or filter.
            </Typography>
          </Paper>
        ) : (
          <>
            <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
              {previewItems.map((order) => (
                <Grid item xs={12} md={6} key={order.order_id}>
                  <GreenSheetCard
                    order={order}
                    onView={() => isMobile
                      ? setMobileDetail({ open: true, id: order.order_id })
                      : navigate(`/home/memberspage/workorder/${order.order_id}`)
                    }
                    statuses={statuses}
                  />
                </Grid>
              ))}
            </Grid>

            <Box
              sx={{
                pt: 3,
                pb: 1,
                display: "flex",
                justifyContent: { xs: "stretch", sm: "space-between" },
                gap: 1.5,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Button
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => setOpenAddModal(true)}
                sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
              >
                Create New Green Sheet
              </Button>

              {filteredWorkOrders.length > 2 && (
                <Button
                  variant="contained"
                  startIcon={<AssignmentTurnedInIcon />}
                  onClick={handleOpen}
                  sx={{
                    backgroundColor: "#2f63ad",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: "none",
                    "&:hover": { backgroundColor: "#1f4f94", boxShadow: "0 8px 20px rgba(47,99,173,0.22)" },
                  }}
                >
                  View All{filteredWorkOrders.length !== workOrders.length
                    ? ` ${filteredWorkOrders.length} Matching`
                    : ""} Green Sheets
                </Button>
              )}
            </Box>
          </>
        )}
      </Box>

      {/* View All Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "18px", boxShadow: "0 18px 60px rgba(0,0,0,0.16)" } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#223142" }}>
          All Green Sheets
          {filteredWorkOrders.length !== workOrders.length && (
            <Typography component="span" variant="body2" sx={{ color: "#6b778c", ml: 1, fontWeight: 400 }}>
              ({filteredWorkOrders.length} of {workOrders.length})
            </Typography>
          )}
        </DialogTitle>

        <DialogContent dividers>
          {/* Filter toolbar inside dialog */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 1.25 }}>
              <TextField
                size="small"
                placeholder="Search green sheets…"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "#6b778c" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: "1 1 200px", "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortOrder}
                  label="Sort By"
                  onChange={(e) => { setSortOrder(e.target.value); setPage(1); }}
                  sx={{ borderRadius: "10px" }}
                >
                  <MenuItem value="newest">Newest First</MenuItem>
                  <MenuItem value="oldest">Oldest First</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {uniqueStatuses.length > 0 && (
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                <Chip
                  label="All"
                  size="small"
                  onClick={() => { setStatusFilter(null); setPage(1); }}
                  sx={{
                    fontWeight: 600,
                    borderRadius: "8px",
                    backgroundColor: statusFilter === null ? "rgba(47,99,173,0.12)" : "rgba(0,0,0,0.05)",
                    color: statusFilter === null ? "#2f63ad" : "#5f6b7a",
                    border: statusFilter === null ? "1px solid rgba(47,99,173,0.3)" : "1px solid transparent",
                  }}
                />
                {uniqueStatuses.map((s) => (
                  <Chip
                    key={s}
                    label={s}
                    size="small"
                    onClick={() => { setStatusFilter(s === statusFilter ? null : s); setPage(1); }}
                    sx={{
                      fontWeight: 600,
                      borderRadius: "8px",
                      backgroundColor: statusFilter === s ? "rgba(47,99,173,0.12)" : "rgba(0,0,0,0.05)",
                      color: statusFilter === s ? "#2f63ad" : "#5f6b7a",
                      border: statusFilter === s ? "1px solid rgba(47,99,173,0.3)" : "1px solid transparent",
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>

          {paginatedWorkOrders.length === 0 ? (
            <Typography variant="body2" sx={{ color: "#6b778c", textAlign: "center", py: 3 }}>
              No green sheets match your search or filter.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {paginatedWorkOrders.map((order) => (
                <Grid item xs={12} key={order.order_id}>
                  <GreenSheetCard
                    order={order}
                    onView={() => {
                      handleClose();
                      isMobile
                        ? setMobileDetail({ open: true, id: order.order_id })
                        : navigate(`/home/memberspage/workorder/${order.order_id}`);
                    }}
                    statuses={statuses}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", flexDirection: "column", gap: 1, p: 2 }}>
          <Pagination
            count={Math.max(1, Math.ceil(filteredWorkOrders.length / ITEMS_PER_PAGE))}
            page={page}
            onChange={(_e, value) => setPage(value)}
            color="primary"
          />
          <Button
            onClick={handleClose}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mobile detail dialog */}
      <Dialog
        open={mobileDetail.open}
        onClose={() => setMobileDetail({ open: false, id: null })}
        fullScreen
        PaperProps={{ sx: { m: 0, borderRadius: 0 } }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            display: "flex",
            justifyContent: "flex-end",
            p: 1,
            backgroundColor: "rgba(255,255,255,0.95)",
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backdropFilter: "blur(4px)",
          }}
        >
          <IconButton onClick={() => setMobileDetail({ open: false, id: null })}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ overflowY: "auto" }}>
          {mobileDetail.id && (
            <MemberWorkordersDetailsheritage
              id={String(mobileDetail.id)}
              onClose={() => setMobileDetail({ open: false, id: null })}
            />
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default WorkOrders;
